<template>
  <div class="cc-milestone">
    <div class="cc-milestone-body">
      <div v-if="loading" class="cc-milestone-loading">
        <i class="fa-duotone fa-spinner-third fa-spin"></i>
        Loading pipeline...
      </div>

      <div v-else class="cc-milestone-shell">
        <div v-if="!items.length" class="cc-milestone-empty">
          <i class="fa-duotone fa-flag-checkered"></i>
          <div class="cc-empty-title">No pipeline items found</div>
          <div class="cc-empty-sub">Try switching scope or check back later</div>
        </div>

        <div v-else class="cc-milestone-list">
          <div class="cc-milestone-scroll">
            <button
              v-for="m in items"
              :key="m.msstatus"
              class="mt-mini cc-ms-mini"
              @click="openMilestone(m)"
            >
              <div class="mt-mini__icon cc-ms-icon">
                <i class="fa-duotone fa-flag-checkered"></i>
              </div>

              <div class="mt-mini__meta">
                <div class="mt-mini__label">{{ m.msstatus }}</div>
                <div class="mt-mini__value">
                  {{ m.loancount }}
                </div>
              </div>

              <i class="fa-solid fa-chevron-right mt-mini__chev"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from "@/api/api.service.js"

export default {
  name: "MilestoneOverviewCard",
  props: {
    azureId: { type: String, required: true },
    accessToken: { type: String, required: true },
    mineOnly: { type: Boolean, default: false },
    orgId: { type: String, default: null }
  },
  data() {
    return {
      loading: false,
      items: []
    }
  },
  watch: {
    azureId: "fetch",
    accessToken: "fetch",
    mineOnly: "fetch",
    orgId: "fetch"
  },
  methods: {
    normalizeMilestoneName(name) {
      const value = (name || "").trim().toLowerCase()

      if (value === "file started" || value === "started") return "Started"

      return name
    },

    async fetch() {
      if (!this.azureId || !this.accessToken) return
      this.loading = true

      const flow = [
        "Started",
        "Ready for Disclosures",
        "Package Received",
        "Sent to Processing",
        "Submitted",
        "Cond'l Approval",
        "Resubmittal",
        "Approved",
        "QC Review",
        "Doc Prep",
        "Doc Signed"
      ]

      const flowIndex = flow.reduce((acc, s, i) => {
        acc[s.toLowerCase()] = i
        return acc
      }, {})

      try {
        const res = await apiService.GetSwiftMilestoneCounts(
          this.azureId,
          this.accessToken,
          this.orgId,
          this.mineOnly
        )

        const raw = res?.data?.data || []
        const grouped = {}

        raw
          .map((x) => ({
            originalStatus: x.msstatus || x.MSSTATUS,
            msstatus: this.normalizeMilestoneName(x.msstatus || x.MSSTATUS),
            loancount: Number(x.loancount ?? x.Loancount ?? 0)
          }))
          .filter((x) => x.msstatus)
          .forEach((x) => {
            if (!grouped[x.msstatus]) {
              grouped[x.msstatus] = {
                msstatus: x.msstatus,
                loancount: 0,
                sourceStatuses: []
              }
            }

            grouped[x.msstatus].loancount += x.loancount

            if (
              x.originalStatus &&
              !grouped[x.msstatus].sourceStatuses.includes(x.originalStatus)
            ) {
              grouped[x.msstatus].sourceStatuses.push(x.originalStatus)
            }
          })

        this.items = Object.values(grouped).sort((a, b) => {
          const ai = flowIndex[a.msstatus.toLowerCase()]
          const bi = flowIndex[b.msstatus.toLowerCase()]

          const aKnown = ai !== undefined
          const bKnown = bi !== undefined

          if (aKnown && bKnown) return ai - bi
          if (aKnown) return -1
          if (bKnown) return 1
          return a.msstatus.localeCompare(b.msstatus)
        })
      } catch (e) {
        this.items = []
      }

      this.loading = false
      this.$emit("loaded", { count: this.items.length, items: this.items })
    },

    async openMilestone(m) {
      if (!m?.sourceStatuses?.length) return
      if (!this.azureId || !this.accessToken) return

      try {
        const results = await Promise.all(
          m.sourceStatuses.map((status) =>
            apiService.GetSwiftMilestoneLoans(
              this.azureId,
              status,
              this.accessToken,
              this.orgId,
              5000,
              this.mineOnly
            )
          )
        )

        const loans = results.flatMap((r) => r?.data?.data || [])
        if (!loans.length) return

        const seen = new Set()
        const dedupedLoans = loans.filter((loan) => {
          const key =
            loan.loan_number ||
            loan.loannumber ||
            loan.encompass_id ||
            loan.guid ||
            JSON.stringify(loan)

          if (seen.has(key)) return false
          seen.add(key)
          return true
        })

        this.$emit("open-milestone-loans", {
          milestone: m,
          loans: dedupedLoans,
          mineOnly: this.mineOnly
        })
      } catch (e) {}
    }
  },
  mounted() {
    this.fetch()
  }
}
</script>

<style scoped>
.cc-milestone {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.cc-milestone-body {
  flex: 1;
  min-height: 0;
  display: flex;
}

.cc-milestone-loading {
  flex: 1;
  min-height: 0;
  width: 100%;
  padding: 18px;
  font-weight: 900;
  font-size: 12px;
  color: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-align: center;
}

.cc-milestone-shell {
  width: 100%;
  height: 100%;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.55);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.cc-milestone-empty {
  flex: 1;
  min-height: 0;
  padding: 26px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  color: rgba(15, 23, 42, 0.6);
}

.cc-milestone-empty i {
  font-size: 22px;
  opacity: 0.85;
}

.cc-empty-title {
  font-weight: 950;
  font-size: 13px;
  color: rgba(15, 23, 42, 0.75);
}

.cc-empty-sub {
  font-weight: 800;
  font-size: 12px;
  color: rgba(15, 23, 42, 0.5);
}

.cc-milestone-list {
  flex: 1;
  min-height: 0;
  display: flex;
  padding: 10px;
}

.cc-milestone-scroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overscroll-behavior: contain;
  padding-right: 6px;
}

.cc-milestone-scroll::-webkit-scrollbar {
  width: 10px;
}

.cc-milestone-scroll::-webkit-scrollbar-track {
  background: rgba(15, 23, 42, 0.04);
  border-radius: 999px;
}

.cc-milestone-scroll::-webkit-scrollbar-thumb {
  background: rgba(15, 23, 42, 0.16);
  border-radius: 999px;
}

.cc-milestone-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(15, 23, 42, 0.22);
}

.cc-ms-mini {
  width: 100%;
  text-align: left;
  border: 1px solid rgba(15, 23, 42, 0.06);
  transition: transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease;
  border-radius: 5px;
}

.cc-ms-mini:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 22px rgba(15, 23, 42, 0.08);
  border-color: rgba(40, 110, 255, 0.22);
}

.cc-ms-mini .mt-mini__label {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cc-ms-icon {
  background: rgba(59, 130, 246, 0.14);
  color: rgba(29, 78, 216, 1);
  box-shadow: inset 0 0 0 1px rgba(59, 130, 246, 0.18);
}

.cc-ms-icon i.fa-duotone {
  --fa-primary-color: rgba(40, 110, 255, 0.95);
  --fa-secondary-color: rgba(40, 110, 255, 0.3);
}
</style>